(() => {
  const READY = () => document.readyState === 'interactive' || document.readyState === 'complete';
  const rm = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  const tracked = new WeakSet();
  const cfgs = [];
  let mx = 0, my = 0;           // latest mouse (client px)
  let raf = 0;                  // rAF loop handle
  let lastT = 0;

  // Map client coords to SVG coords
  function toSvg(svg, x, y) {
    const pt = svg.createSVGPoint(); pt.x = x; pt.y = y;
    const inv = svg.getScreenCTM()?.inverse?.();
    return inv ? pt.matrixTransform(inv) : { x: 0, y: 0 };
  }

  // Smooth “dead zone” + “soft saturation”.
  // dead: SVG units where motion is suppressed around center.
  // max: clamp distance along axis.
  function applyDeadSoft(x, dead, max) {
    const ax = Math.abs(x);
    if (ax <= dead) return 0;
    const s = Math.sign(x);
    let u = (ax - dead) / Math.max(1e-6, (max - dead)); // 0..1
    if (u > 1) u = 1;
    // smoothstep for a gentle curve
    u = u * u * (3 - 2 * u);
    return s * u * max;
  }

  // Start/stop main loop depending on activity
  function ensureLoop() {
    if (!raf) {
      lastT = performance.now();
      raf = requestAnimationFrame(tick);
    }
  }
  function maybeStopLoop() {
    if (!cfgs.length) { cancelAnimationFrame(raf); raf = 0; return; }
    // Stop if everyone is essentially at rest
    for (const c of cfgs) {
      if (Math.abs(c.v) > 0.001 || Math.abs(c.s - c.sTarget) > 0.001) { return; }
    }
    cancelAnimationFrame(raf);
    raf = 0;
  }

  function tick(t) {
    const dt = Math.min(0.05, (t - lastT) / 1000 || 0.016); // clamp dt
    lastT = t;

    for (const c of cfgs) {
      // Compute target along axis from latest mouse
      const m = toSvg(c.svg, mx, my);
      const vx = m.x - c.p0x, vy = m.y - c.p0y;
      const proj = vx * c.ux + vy * c.uy; // signed distance along axis

      // Target with dead-zone & soft saturation
  c.sTarget = applyDeadSoft(proj, c.dead, Math.max(c.maxF, c.maxB)) + c.bias;

  

      // Critically-damped-ish spring: v += k*(target - s) - d*v; s += v
      const k = c.k;             // stiffness (higher = snappier)
      const d = c.d;             // damping 0..1 (higher = more damped)
      const a = k * (c.sTarget - c.s) - (d * c.v);
      c.v += a * dt;
      c.s += c.v * dt;

      if (c.s >  c.maxF) c.s =  c.maxF;
if (c.s < -c.maxB) c.s = -c.maxB;


      // Final clamp (safety) and write transform
      if (c.s > c.max) c.s = c.max; else if (c.s < -c.max) c.s = -c.max;
      c.el.setAttribute(
        'transform',
        `${c.base} translate(${(c.ux * c.s).toFixed(3)} ${(c.uy * c.s).toFixed(3)})`
      );
    }

    raf = requestAnimationFrame(tick);
    maybeStopLoop();
  }

  function onMove(e) { mx = e.clientX; my = e.clientY; ensureLoop(); }

  function reset() {
    for (const c of cfgs) { c.s = 0; c.v = 0; c.sTarget = 0; c.el.setAttribute('transform', c.base); }
  }

function makeConfig(el) {
  const svg = el.ownerSVGElement;
  if (!svg || !svg.getScreenCTM) return null;

  const deg  = parseFloat(el.getAttribute('data-axis-deg') || '0') || 0;
  const max  = parseFloat(el.getAttribute('data-max') || '10') || 10;

  // NEW: allow asymmetric limits + bias
  const maxF = parseFloat(el.getAttribute('data-max-forward') || '') || max; // forward (+)
  const maxB = parseFloat(el.getAttribute('data-max-back')    || '') || max; // backward (-)
  const bias = parseFloat(el.getAttribute('data-bias')        || '0') || 0;  // constant offset along axis

  const dead = Math.max(0, parseFloat(el.getAttribute('data-deadzone') || '0')) || 0;
  const k = parseFloat(el.getAttribute('data-stiffness') || '18');
  const d = parseFloat(el.getAttribute('data-damping')   || '8');

  const rad = (deg * Math.PI) / 180;
  const ux = Math.cos(rad), uy = Math.sin(rad);

  // Anchor (robust): data-anchor="x,y" OR data-anchor-x / data-anchor-y
  let p0x, p0y;
  const anchorAttr = el.getAttribute('data-anchor');
  const axAttr = el.getAttribute('data-anchor-x');
  const ayAttr = el.getAttribute('data-anchor-y');

  if (anchorAttr && anchorAttr.includes(',')) {
    const [ax, ay] = anchorAttr.split(',').map(parseFloat);
    p0x = ax; p0y = ay;
  } else if (axAttr !== null && ayAttr !== null) {
    const ax = parseFloat(axAttr), ay = parseFloat(ayAttr);
    if (!Number.isNaN(ax) && !Number.isNaN(ay)) { p0x = ax; p0y = ay; }
  }

  // Fallback to geometric center if no valid anchor provided
  if (p0x === undefined || p0y === undefined) {
    const bb = el.getBBox();
    const cx = bb.x + bb.width / 2, cy = bb.y + bb.height / 2;
    const ctm = el.getCTM();
    if (ctm) { p0x = ctm.a*cx + ctm.c*cy + ctm.e; p0y = ctm.b*cx + ctm.d*cy + ctm.f; }
    else { p0x = cx; p0y = cy; }
  }

  return {
    svg, el,
    base: el.getAttribute('transform') || '',
    ux, uy,
    max, maxF, maxB, bias,
    dead, k, d, p0x, p0y,
    s: 0, v: 0, sTarget: 0
  };
}


  function scan(root = document) {
    if (rm()) return;
    const nodes = root.querySelectorAll?.('svg [data-axis-deg]') || [];
    nodes.forEach(el => {
      if (tracked.has(el)) return;
      const cfg = makeConfig(el);
      if (!cfg) return;
      tracked.add(el);
      cfgs.push(cfg);
    });
  }

  function wire() {
    if (rm()) return;
    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('mousemove', onMove, { passive: true }); // fallback
    window.addEventListener('mouseleave', reset);
    window.addEventListener('blur', reset);

    scan(document);

    const mo = new MutationObserver(m => {
      for (const r of m) r.addedNodes?.forEach(n => { if (n.nodeType === 1) scan(n); });
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });

    document.addEventListener('shopify:section:load',  e => scan(e.target));
    document.addEventListener('shopify:section:select',e => scan(e.target));
  }

  if (READY()) wire(); else document.addEventListener('DOMContentLoaded', wire);
})();
